import { useRef, useState } from 'react';
import {
  Page, Card, IndexTable, Text, Badge, Banner, Button, Box, InlineStack,
  BlockStack, ChoiceList, DropZone, Spinner, Toast, Select, Divider, Tooltip,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listImports, uploadImport, triggerVendorPull, type ImportJob, type ImportSource, type VendorPullOption } from '../api/imports';
import { ago } from './WheelsList';

// Responsive: IndexTable with 9 narrow columns overflows on anything
// below ~900px, so on small screens we render a stacked card list instead.
// The media query is handled in the style block appended at the bottom.
export function ImportsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [source, setSource] = useState<ImportSource>('vehicles');
  const [vendorForWheels, setVendorForWheels] = useState<'ATD' | 'WheelPros'>('ATD');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [file, setFile] = useState<File | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['imports'],
    queryFn: listImports,
    // Poll every 3s while anything's moving, also every 15s as a steady-
    // state heartbeat so finished jobs + external triggers (vendor pulls
    // queued from the home page, worker auto-applies) show up without a
    // manual Refresh click.
    refetchInterval: (query) => {
      const rows = (query.state.data as { data?: ImportJob[] } | undefined)?.data;
      if (rows?.some((j) => j.status === 'pending' || j.status === 'processing')) return 3000;
      return 15000;
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  // Manual refresh: invalidate the cache too so all imports-related queries
  // across the app refetch (home page recent-jobs, this page, detail polls).
  const manualRefresh = () => {
    qc.invalidateQueries({ queryKey: ['imports'] });
    void refetch();
  };

  const upload = useMutation({
    mutationFn: ({ file, source, vendor }: { file: File; source: ImportSource; vendor?: string }) =>
      uploadImport(file, source, vendor),
    onSuccess: (resp) => {
      setToast('Upload started');
      setFile(null);
      qc.invalidateQueries({ queryKey: ['imports'] });
      navigate(`/imports/${resp.id}`);
    },
  });

  const pullVendor = useMutation<
    { id: number; vendor: VendorPullOption }, Error, VendorPullOption
  >({
    mutationFn: (vendor) => triggerVendorPull(vendor),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['imports'] });
      navigate(`/imports/${r.id}`);
    },
  });

  const jobs = data?.data ?? [];
  const vendorLabel = source === 'wheels' ? vendorForWheels : 'RedBook';

  // Vendor filter — derive distinct vendors from the job list itself so the
  // dropdown stays in sync with what's actually been ingested. Empty/null
  // source_guides bucket under "(unknown)" rather than silently disappearing.
  const distinctVendors = Array.from(
    new Set(jobs.map((j) => (j.source_guide && j.source_guide.trim()) || '(unknown)')),
  ).sort();
  const filteredJobs =
    vendorFilter === 'all'
      ? jobs
      : jobs.filter((j) => ((j.source_guide && j.source_guide.trim()) || '(unknown)') === vendorFilter);
  const vendorFilterOptions = [
    { label: `All vendors (${jobs.length})`, value: 'all' },
    ...distinctVendors.map((v) => ({
      label: `${v} (${jobs.filter((j) => ((j.source_guide && j.source_guide.trim()) || '(unknown)') === v).length})`,
      value: v,
    })),
  ];

  return (
    <>
      <Page
        title="Wheel imports"
        subtitle="Pull fresh wheel feeds from ATD or Wheel Pros, or upload a wheel CSV. Tires have their own pipeline at Tire imports."
      >
        {error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical">Failed to load imports: {(error as Error).message}</Banner>
          </Box>
        )}

        <BlockStack gap="400">
          {/* ——— Vendor pull shortcuts ——— */}
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">Pull fresh wheel feed</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Wheels only. For tires, use Tire imports in the left nav. Runs in the background, progress shown below.
              </Text>
              <div className="rr-pull-grid">
                <Button
                  variant="primary"
                  loading={pullVendor.isPending && pullVendor.variables === 'ATD'}
                  onClick={() => {
                    if (!confirm('Queue a fresh ATD wheel feed pull? Takes ~5-10 min. (Tires are pulled from Tire imports — not here.)')) return;
                    pullVendor.mutate('ATD');
                  }}
                >Pull ATD wheels</Button>
                <Button
                  variant="primary"
                  loading={pullVendor.isPending && pullVendor.variables === 'WheelPros'}
                  onClick={() => {
                    if (!confirm('Queue a fresh Wheel Pros wheel catalog pull (includes MAP prices)?')) return;
                    pullVendor.mutate('WheelPros');
                  }}
                >Pull Wheel Pros wheels</Button>
              </div>
              {pullVendor.error && <Banner tone="critical">{pullVendor.error.message}</Banner>}
            </BlockStack>
          </Card>

          {/* ——— Manual CSV upload ——— */}
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">Upload a CSV</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Only use when a vendor feed is down or you need to ingest a hand-curated file.
              </Text>
              <div className="rr-upload-grid">
                <div>
                  <ChoiceList
                    title="Entity"
                    choices={[
                      { label: 'Vehicles (RedBook fitment guide)', value: 'vehicles' },
                      { label: 'Wheels (vendor catalog)', value: 'wheels' },
                    ]}
                    selected={[source]}
                    onChange={(selected) => setSource((selected[0] as ImportSource) ?? 'vehicles')}
                  />
                </div>
                {source === 'wheels' && (
                  <div>
                    <Select
                      label="Vendor"
                      options={[
                        { label: 'ATD', value: 'ATD' },
                        { label: 'Wheel Pros', value: 'WheelPros' },
                      ]}
                      value={vendorForWheels}
                      onChange={(v) => setVendorForWheels(v as 'ATD' | 'WheelPros')}
                    />
                  </div>
                )}
              </div>
              <DropZone
                accept=".csv,text/csv"
                type="file"
                allowMultiple={false}
                onDrop={(_, accepted) => setFile(accepted[0] ?? null)}
              >
                {file
                  ? <Box padding="400"><Text as="p" variant="bodyMd">{file.name} — {(file.size / 1024).toFixed(1)} KB</Text></Box>
                  : <DropZone.FileUpload actionHint="CSV only, up to 200 MB" />}
              </DropZone>
              <InlineStack gap="200" wrap>
                <Button
                  variant="primary"
                  disabled={!file || upload.isPending}
                  loading={upload.isPending}
                  onClick={() => file && upload.mutate({
                    file,
                    source,
                    vendor: source === 'wheels' ? vendorForWheels : undefined,
                  })}
                >Upload as {vendorLabel} {source}</Button>
                {file && <Button onClick={() => setFile(null)}>Clear</Button>}
              </InlineStack>
              {upload.error && <Banner tone="critical">Upload failed: {(upload.error as Error).message}</Banner>}
            </BlockStack>
          </Card>

          {/* ——— Recent jobs ——— */}
          <Card padding="0">
            <Box padding="400" borderBlockEndWidth="025" borderColor="border">
              <InlineStack align="space-between" blockAlign="center" wrap gap="300">
                <Text as="h3" variant="headingMd">Recent jobs</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Select
                    label="Filter by vendor"
                    labelHidden
                    options={vendorFilterOptions}
                    value={vendorFilter}
                    onChange={setVendorFilter}
                  />
                  <Button onClick={manualRefresh} loading={isLoading || isFetching}>Refresh</Button>
                </InlineStack>
              </InlineStack>
            </Box>
            {isLoading ? (
              <Box padding="600"><InlineStack align="center"><Spinner /></InlineStack></Box>
            ) : jobs.length === 0 ? (
              <Box padding="600"><Text as="p" tone="subdued" alignment="center">No imports yet.</Text></Box>
            ) : filteredJobs.length === 0 ? (
              <Box padding="600">
                <Text as="p" tone="subdued" alignment="center">
                  No imports for vendor "{vendorFilter}".
                </Text>
              </Box>
            ) : (
              <>
                {/* Desktop / wide — IndexTable */}
                <div className="rr-jobs-wide">
                  <IndexTable
                    resourceName={{ singular: 'import', plural: 'imports' }}
                    itemCount={filteredJobs.length}
                    selectable={false}
                    headings={[
                      { title: 'ID' }, { title: 'Source' }, { title: 'File' },
                      { title: 'Status' }, { title: 'New' }, { title: 'Update' },
                      { title: 'Dup' }, { title: 'Invalid' }, { title: 'Created' },
                    ]}
                  >
                    {filteredJobs.map((job, i) => {
                      const isPriceSync = job.job_kind === 'price_sync';
                      const sourceLabel = isPriceSync ? 'Price sync' : (job.source_guide ?? job.source ?? '');
                      const fileLabel = isPriceSync
                        ? 'Shopify price diff'
                        : (job.filename === 'vendor-pull' ? 'vendor pull' : job.filename);
                      return (
                        <IndexTable.Row id={String(job.id)} key={job.id} position={i} onClick={() => navigate(`/imports/${job.id}`)}>
                          <IndexTable.Cell>{job.id}</IndexTable.Cell>
                          <IndexTable.Cell><Badge>{sourceLabel}</Badge></IndexTable.Cell>
                          <IndexTable.Cell>
                            <Text as="span" variant="bodySm" tone="subdued">{fileLabel}</Text>
                          </IndexTable.Cell>
                          <IndexTable.Cell><StatusBadge status={job.status} job={job} /></IndexTable.Cell>
                          {/*
                            Price-sync rows don't have New/Update/Dup/Invalid counts that mean
                            what those column headers imply. Show a single concise stat in the
                            "Update" cell and dash the others, so the row is unambiguous to read.
                          */}
                          <IndexTable.Cell>{isPriceSync ? <DashCell /> : job.new_count.toLocaleString()}</IndexTable.Cell>
                          <IndexTable.Cell>
                            {isPriceSync
                              ? <PriceSyncStat job={job} />
                              : job.update_count.toLocaleString()}
                          </IndexTable.Cell>
                          <IndexTable.Cell>{isPriceSync ? <DashCell /> : job.duplicate_count.toLocaleString()}</IndexTable.Cell>
                          <IndexTable.Cell>
                            {isPriceSync
                              ? <DashCell />
                              : (job.invalid_count > 0
                                  ? <Badge tone="critical">{job.invalid_count.toLocaleString()}</Badge>
                                  : job.invalid_count.toLocaleString())}
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <Text as="span" variant="bodySm" tone="subdued">{ago(job.created_at)}</Text>
                          </IndexTable.Cell>
                        </IndexTable.Row>
                      );
                    })}
                  </IndexTable>
                </div>

                {/* Mobile / narrow — stacked cards */}
                <div className="rr-jobs-narrow">
                  <BlockStack gap="0">
                    {filteredJobs.map((job, idx) => {
                      const isPriceSync = job.job_kind === 'price_sync';
                      return (
                        <div key={job.id} onClick={() => navigate(`/imports/${job.id}`)} style={{ cursor: 'pointer' }}>
                          <Box padding="400">
                            <BlockStack gap="200">
                              <InlineStack gap="200" blockAlign="center" wrap>
                                <Text as="span" variant="bodyMd" fontWeight="bold">#{job.id}</Text>
                                <Badge>{isPriceSync ? 'Price sync' : (job.source_guide ?? job.source ?? '')}</Badge>
                                <StatusBadge status={job.status} job={job} />
                              </InlineStack>
                              <Text as="p" variant="bodySm" tone="subdued" breakWord>
                                {isPriceSync
                                  ? 'Shopify price diff'
                                  : (job.filename === 'vendor-pull' ? 'vendor pull' : job.filename)}
                              </Text>
                              {isPriceSync ? (
                                <PriceSyncStat job={job} />
                              ) : (
                                <InlineStack gap="300" wrap>
                                  <MiniStat label="New" value={job.new_count} />
                                  <MiniStat label="Upd" value={job.update_count} />
                                  <MiniStat label="Dup" value={job.duplicate_count} />
                                  <MiniStat label="Invalid" value={job.invalid_count} critical={job.invalid_count > 0} />
                                </InlineStack>
                              )}
                              <Text as="span" variant="bodySm" tone="subdued">{ago(job.created_at)}</Text>
                            </BlockStack>
                          </Box>
                          {idx < filteredJobs.length - 1 && <Divider />}
                        </div>
                      );
                    })}
                  </BlockStack>
                </div>
              </>
            )}
          </Card>
        </BlockStack>
        {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} />
      </Page>

      {/*
        Responsive breakpoint — Polaris layout is flex / grid friendly, but
        IndexTable needs horizontal space or it clips. Below 720px viewport
        we hide the table and show the stacked card list instead. The grid
        helpers flatten to single columns on the same breakpoint.
      */}
      <style>{`
        .rr-pull-grid, .rr-upload-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }
        .rr-jobs-wide { display: block; }
        .rr-jobs-narrow { display: none; }
        @media (max-width: 720px) {
          .rr-pull-grid, .rr-upload-grid { grid-template-columns: 1fr; }
          .rr-jobs-wide { display: none; }
          .rr-jobs-narrow { display: block; }
        }
      `}</style>
    </>
  );
}

function MiniStat({ label, value, critical }: { label: string; value: number; critical?: boolean }) {
  return (
    <InlineStack gap="100" blockAlign="center">
      <Text as="span" variant="bodySm" tone="subdued">{label}:</Text>
      {critical ? <Badge tone="critical">{value.toLocaleString()}</Badge>
        : <Text as="span" variant="bodySm">{value.toLocaleString()}</Text>}
    </InlineStack>
  );
}

function DashCell() {
  return <Text as="span" variant="bodySm" tone="subdued">—</Text>;
}

// A tiny one-line stat to put in the "Update" column for price-sync rows.
// Reads applied/failed straight off invalid_count + update_count (which is
// what the script writes today) so it works even before metadata is in place.
function PriceSyncStat({ job }: { job: ImportJob }) {
  const applied = job.update_count ?? 0;
  const invalid = job.invalid_count ?? 0;
  if (invalid > 0 && applied > 0) {
    return <Text as="span" variant="bodySm">{applied.toLocaleString()} synced · <Text as="span" tone="critical">{invalid} failed</Text></Text>;
  }
  if (invalid > 0) {
    return <Text as="span" variant="bodySm" tone="critical">{invalid.toLocaleString()} failed</Text>;
  }
  if (applied > 0) return <Text as="span" variant="bodySm">{applied.toLocaleString()} synced</Text>;
  return <Text as="span" variant="bodySm" tone="subdued">no changes</Text>;
}

// Price-sync rows (map-shopify-sync / price-shopify-sync) don't fit the
// New/Update/Dup/Invalid model — they're a diff-only price propagator.
// Render a single status badge that means something. Driven by `health`
// decorated by the API.
function PriceSyncBadge({ job }: { job: ImportJob }) {
  const h = job.health;
  if (h === 'healthy')              return <Tooltip content="Prices synced"><Badge tone="success">Synced</Badge></Tooltip>;
  if (h === 'no_drift')             return <Tooltip content="No price drift — Shopify already matches catalog"><Badge tone="info">In sync</Badge></Tooltip>;
  if (h === 'partial')              return <Tooltip content="Some prices synced, some failed — open for details"><Badge tone="warning">Partial</Badge></Tooltip>;
  if (h === 'scanned_zero')         return <Tooltip content="Run touched zero rows — sync may be misconfigured"><Badge tone="critical">Scanned 0</Badge></Tooltip>;
  if (h === 'no_variants_returned') return <Tooltip content="Shopify returned nothing for the scanned variants"><Badge tone="critical">No variants</Badge></Tooltip>;
  if (h === 'failed')               return <Badge tone="critical">Failed</Badge>;
  // Job still processing or no health yet
  if (job.status === 'processing')  return <Badge tone="attention">Running</Badge>;
  return <Badge>{job.status}</Badge>;
}

function StatusBadge({ status, job }: { status: ImportJob['status']; job?: ImportJob }) {
  // Price sync rows get their own badge driven by metadata.health, not status.
  if (job && job.job_kind === 'price_sync') return <PriceSyncBadge job={job} />;
  // Special case: WheelProsInventory SFTP scans that find no file are
  // technically 'applied' (status=applied, row_count=0) but visually they
  // look like wasted successful runs. Render as a subdued 'no file' badge
  // with a tooltip surfacing the diagnostic blob (dirs scanned, candidates
  // seen, newest file) so the user can tell at a glance whether the SFTP
  // is empty or our scanner is missing the file's location.
  if (
    status === 'applied' &&
    job &&
    job.source_guide === 'WheelProsInventory' &&
    job.filename !== 'vendor-pull-from-url' &&
    (job.row_count ?? 0) === 0
  ) {
    const meta = (job.metadata ?? null) as null | {
      searched_dirs?: string[];
      fallback_dirs?: string[];
      candidates_seen?: { path: string; modifyTime: number; matched_filename_pattern: boolean }[];
      matched_candidates?: unknown[];
      newest_file_seen?: { path: string; modifyTime: number } | null;
      error?: string | null;
    };
    if (!meta) {
      return (
        <Tooltip content="Scanner ran but no diagnostic was stored (pre-migration job).">
          <Badge tone="info">no file</Badge>
        </Tooltip>
      );
    }
    const dirs = (meta.searched_dirs?.length ?? 0) + (meta.fallback_dirs?.length ?? 0);
    const seen = meta.candidates_seen?.length ?? 0;
    const newest = meta.newest_file_seen?.modifyTime
      ? new Date(meta.newest_file_seen.modifyTime).toLocaleString()
      : 'none';
    const tip = meta.error
      ? `SFTP error: ${meta.error}`
      : `Scanned ${dirs} dirs · saw ${seen} candidate file(s) · newest: ${newest}`;
    return (
      <Tooltip content={tip}>
        <Badge tone="info">no file</Badge>
      </Tooltip>
    );
  }
  const map: Record<ImportJob['status'], 'info' | 'success' | 'warning' | 'critical' | 'attention'> = {
    pending: 'info',
    processing: 'attention',
    preview: 'warning',
    applied: 'success',
    failed: 'critical',
    rolled_back: 'critical',
  };
  return <Badge tone={map[status]}>{status}</Badge>;
}

function PullVendorButtons() {
  const navigate = (typeof window !== 'undefined') ? (p: string) => { window.location.href = p; } : () => {};
  const pullM = useMutation({ mutationFn: (vendor: 'ATD' | 'WheelPros' | 'WheelProsInventory') => triggerVendorPull(vendor) });
  const click = (v: 'ATD' | 'WheelPros' | 'WheelProsInventory') => {
    if (!confirm(`Pull ${v}? Runs in background — watch progress in this list.`)) return;
    pullM.mutate(v, {
      onSuccess: (r: any) => navigate(r?.id ? `/imports/${r.id}` : '/imports'),
    });
  };
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h3" variant="headingMd">Pull fresh from vendor</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              One-click vendor pull. Runs in the background; track progress in the import list below.
            </Text>
          </BlockStack>
        </InlineStack>
        <InlineStack gap="200" wrap>
          <Button loading={pullM.isPending && pullM.variables === 'WheelPros'} onClick={() => click('WheelPros')}>
            Pull Wheel Pros catalog
          </Button>
          <Button loading={pullM.isPending && pullM.variables === 'WheelProsInventory'} onClick={() => click('WheelProsInventory')}>
            Pull Wheel Pros inventory
          </Button>
          <Button loading={pullM.isPending && pullM.variables === 'ATD'} onClick={() => click('ATD')}>
            Pull ATD
          </Button>
        </InlineStack>
        {pullM.error && (
          <Banner tone="critical">{(pullM.error as Error).message}</Banner>
        )}
      </BlockStack>
    </Card>
  );
}

